import { useState } from "react";
import { api, qs } from "../api.js";
import { fmt, t } from "../i18n.js";
import { isManager, useMe } from "../shared.jsx";
import { ErrorBox, Field, useAction, useLoad } from "../ui.jsx";

const KINDS = [["project", "تقرير مشروع"], ["team", "تقرير فريق"], ["employee", "تقرير موظف"], ["delays", "التأخير والعوائق"],
  ["kpi", "التقييم الشهري"]];

function thisMonth() {
  const d = new Date(Date.now() + 2 * 36e5);
  return d.toISOString().slice(0, 7);
}

export function Reports() {
  const me = useMe();
  const [kind, setKind] = useState("project");
  const [f, setF] = useState({ id: "", from: "", to: "", status: "", priority: "", month: thisMonth() });
  const [report, setReport] = useState(null);
  const { busy, error, run } = useAction();
  const projects = useLoad(() => api.get("/projects?page_size=100&include_archived=1"), []);
  const teams = useLoad(() => api.get("/teams?page_size=100"), []);
  const users = useLoad(() => api.get("/users?page_size=100"), []);
  const needsId = ["project", "team", "employee"].includes(kind);
  const params = kind === "kpi" ? { month: f.month } : { from: f.from, to: f.to, status: f.status, priority: f.priority };
  const show = () => run(() => api.get(`/reports/${kind}${needsId ? "/" + f.id : ""}` + qs(params))).then(setReport, () => setReport(null));
  const exportAs = (format) => run(() => api.download(`/reports/export/${kind}` + qs({ ...params, id: needsId ? f.id : undefined, format }),
    `${KINDS.find((k) => k[0] === kind)[1]}.${format === "html" ? "html" : format}`), "بدأ التنزيل").catch(() => {});
  const options = { project: projects.data?.items.map((p) => [p.id, `${p.ref_code} — ${p.name}`]),
    team: teams.data?.items.map((x) => [x.id, x.name]), employee: users.data?.items.map((u) => [u.id, u.name]) }[kind] || [];
  const allowed = isManager(me) ? KINDS : KINDS.filter(([k]) => k !== "project");
  return (
    <div className="page">
      <header className="page-head"><h1>{t("reports")}</h1></header>
      <section className="panel">
        <div className="seg seg-wrap">{allowed.map(([k, l]) => (
          <button key={k} className={"seg-btn" + (kind === k ? " seg-on" : "")} onClick={() => { setKind(k); setReport(null); setF({ ...f, id: "" }); }}>{l}</button>))}</div>
        <div className="form-grid">
          {needsId && <Field label={KINDS.find((k) => k[0] === kind)[1].replace("تقرير ", "")}>
            <select value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })}><option value="">اختر</option>
              {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>}
          {kind === "kpi" ? <Field label="الشهر"><input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} /></Field> : <>
            <Field label="موعد التسليم من"><input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
            <Field label="إلى"><input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
            <Field label="الحالة"><select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="">الكل</option>
              {Object.keys(t("status")).map((s) => <option key={s} value={s}>{t("status." + s)}</option>)}</select></Field>
            <Field label="الأولوية"><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}><option value="">الكل</option>
              {Object.keys(t("priority")).map((s) => <option key={s} value={s}>{t("priority." + s)}</option>)}</select></Field>
          </>}
        </div>
        <div className="actions">
          <button className="btn btn-primary" disabled={busy || (needsId && !f.id)} onClick={show}>عرض التقرير</button>
          {["xlsx", "csv", "pdf", "html"].map((fm) => (
            <button key={fm} className="btn" disabled={busy || (needsId && !f.id)} onClick={() => exportAs(fm)}>
              {{ xlsx: "Excel", csv: "CSV", pdf: "PDF", html: "نسخة طباعة" }[fm]}</button>))}
        </div>
        <ErrorBox error={error} />
      </section>
      {report && <ReportView r={report} />}
    </div>
  );
}

function ReportView({ r }) {
  if (r.kind === "kpi") {
    return (
      <section className="panel">
        <h2>{r.title} {r.status === "open" && <small className="muted">(مبدئي — الشهر غير مغلق)</small>}</h2>
        <table className="table"><thead><tr><th>الموظف</th><th>النتيجة</th><th>الالتزام</th><th>الجودة</th><th>التوثيق</th><th>حجم العمل</th><th>مهام معتمدة</th></tr></thead>
          <tbody>{r.items.map((i) => (
            <tr key={i.user.id}><td><a href={`#/eval?user=${i.user.id}&month=${r.month}`}>{i.user.name}</a></td>
              <td><b>{i.sufficient ? fmt.num(i.overall, 2) : "لا توجد بيانات كافية"}</b></td>
              {["punctuality", "quality", "documentation", "workload"].map((k) => <td key={k}>{fmt.num(i.components[k], 1)}</td>)}
              <td>{i.approved_task_count}</td></tr>))}</tbody></table>
        <p className="muted small">{r.note}</p>
      </section>
    );
  }
  const s = r.summary;
  return (
    <>
      <div className="kpis">
        <div className="kpi"><b>{s.total}</b>مهمة</div>
        <div className="kpi"><b>{s.by_status.approved}</b>معتمدة</div>
        <div className="kpi"><b className={s.overdue ? "late" : ""}>{s.overdue}</b>متأخرة</div>
        <div className="kpi"><b>{s.weighted_completion == null ? "—" : s.weighted_completion + "%"}</b>إنجاز موزون</div>
      </div>
      {r.people && (
        <section className="panel"><h2>حسب الموظف</h2>
          <table className="table"><thead><tr><th>الموظف</th><th>المهام</th><th>المعتمدة</th><th>المفتوحة</th><th>المتأخرة</th><th>الوزن المفتوح</th><th>الإعادات</th></tr></thead>
            <tbody>{r.people.map((p) => <tr key={p.user_id}><td>{p.name}</td><td>{p.tasks}</td><td>{p.approved}</td><td>{p.open}</td>
              <td className={p.overdue ? "late" : ""}>{p.overdue}</td><td>{p.open_weight}</td><td>{p.returns}</td></tr>)}</tbody></table></section>
      )}
      <section className="panel"><h2>{r.title}</h2>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>المهمة</th><th>المشروع</th><th>المسؤول</th><th>الحالة</th><th>الموعد المعدّل</th><th>مستبعد (س)</th><th>إعادات</th></tr></thead>
          <tbody>{r.tasks.map((x) => (
            <tr key={x.id}><td><a href={`#/tasks/${x.id}`}>{x.ref} {x.title}</a></td><td>{x.project_name}</td><td>{x.assignee_name}</td>
              <td>{x.status_label}</td><td className={x.overdue ? "late" : ""}>{fmt.dt(x.adjusted_due_at)}</td><td>{x.excluded_hours || ""}</td><td>{x.returns || ""}</td></tr>))}</tbody>
        </table></div>
      </section>
      {r.obstacles?.length > 0 && (
        <section className="panel"><h2>العوائق</h2>
          <table className="table"><thead><tr><th>المهمة</th><th>السبب</th><th>الحالة</th><th>الساعات المعتمدة</th></tr></thead>
            <tbody>{r.obstacles.map((o) => <tr key={o.id}><td>{o.ref} {o.title}</td><td>{o.reason_label}</td><td>{t("obstacleStatus." + o.status)}</td><td>{o.approved_hours ?? "—"}</td></tr>)}</tbody></table>
        </section>
      )}
    </>
  );
}
